import { differenceInCalendarYears, format, startOfWeek, subDays, } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
/**
 * Creates a date representing the start of year in UTC
 */
export function startOfYearUTC(date) {
    return new Date(Date.UTC(date.getUTCFullYear(), 0, 1, 0, 0, 0, 0));
}
/**
 * Calculates the difference in calendar days between two dates using UTC
 */
export function differenceInCalendarDaysUTC(laterDate, earlierDate) {
    const laterUTC = new Date(laterDate.toISOString());
    const earlierUTC = new Date(earlierDate.toISOString());
    laterUTC.setUTCHours(0, 0, 0, 0);
    earlierUTC.setUTCHours(0, 0, 0, 0);
    const msPerDay = 24 * 60 * 60 * 1000;
    return Math.round((laterUTC.getTime() - earlierUTC.getTime()) / msPerDay);
}
/**
 * Calculates the difference in calendar years between two dates using UTC
 */
export function differenceInCalendarYearsUTC(from, to) {
    const fromUTC = new Date(from.toISOString());
    const toUTC = new Date(to.toISOString());
    fromUTC.setUTCHours(0, 0, 0, 0);
    toUTC.setUTCHours(0, 0, 0, 0);
    const yearsDiff = differenceInCalendarYears(toUTC, fromUTC);
    return Math.round(yearsDiff);
}
/**
 * Flexible date formatting function using date-fns
 *
 * @param date - Date string, Date object, or null/undefined
 * @param dateFormat - Format string (use DATE_FORMATS constants)
 * @param fallback - String to return if date is invalid
 * @returns Formatted date string or fallback
 */
export function formatDateDisplay(date, dateFormat = 'yyyy-MM-dd', fallback = 'Unknown date') {
    if (!date) {
        return fallback;
    }
    try {
        const dateObj = typeof date === 'string' ? new Date(date) : date;
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
            return fallback;
        }
        return format(dateObj, dateFormat);
    }
    catch (error) {
        return fallback;
    }
}
/**
 * Normalizes date filter values to YYYY-MM-DD format for consistent querying
 * across frontend and backend
 *
 * Converts date strings or Date objects to simple YYYY-MM-DD format in UTC.
 * This simplifies date handling by removing time components and timezone complexity.
 *
 * @example
 * ```typescript
 * const filters = normalizeDateFilters({
 *   fromDate: '2024-01-15T10:30:00.000Z',
 *   toDate: '2024-01-20T14:45:00.000Z'
 * });
 * // If `ignoreTime` is true, Returns:
 * // {
 * //   fromDate: '2024-01-15',
 * //   toDate: '2024-01-20'
 * // }
 *
 * // If `ignoreTime` is false, Returns:
 * // {
 * //   fromDate: '2024-01-15T10:30:00.000Z',
 * //   toDate: '2024-01-20T14:45:00.000Z'
 * // }
 * ```
 */
export function normalizeDateFilters(filters, ignoreTime = false) {
    const normalized = { ...filters };
    if (normalized.fromDate && typeof normalized.fromDate === 'string') {
        normalized.fromDate = ignoreTime
            ? formatDateDisplay(normalized.fromDate)
            : new Date(normalized.fromDate)?.toISOString();
    }
    if (normalized.toDate && typeof normalized.toDate === 'string') {
        normalized.toDate = ignoreTime
            ? formatDateDisplay(normalized.toDate)
            : new Date(normalized.toDate)?.toISOString();
    }
    return normalized;
}
/**
 * Get the start date for a preset filter (inclusive of today)
 * @param preset - The quick filter preset
 * @param today - Optional today date (defaults to new Date())
 * @returns The from date for the preset
 */
export function getPresetFromDate(preset, today = new Date()) {
    switch (preset) {
        case 'last-7d':
            return subDays(today, 6); // 7 days including today
        case 'last-30d':
            return subDays(today, 29); // 30 days including today
        case 'last-90d':
            return subDays(today, 89); // 90 days including today
        case 'last-1y':
            return subDays(today, 364); // 365 days including today
        case 'ytd':
            return startOfYearUTC(today); // Jan 1 to today
        default:
            return today;
    }
}
/**
 * Detect which preset matches the given date range
 * @param fromDate - Start date (YYYY-MM-DD string or Date)
 * @param toDate - End date (YYYY-MM-DD string or Date)
 * @returns The matching preset or 'custom' for non-matching ranges
 */
export function detectPresetFromDateRange(fromDate, toDate) {
    if (!fromDate || !toDate) {
        return 'last-30d';
    }
    const from = typeof fromDate === 'string' ? new Date(fromDate) : fromDate;
    const to = typeof toDate === 'string' ? new Date(toDate) : toDate;
    const daysDiff = differenceInCalendarDaysUTC(to, from);
    // Check for Last 7 days (span of 6 days for 7 calendar days)
    if (daysDiff === 6) {
        return 'last-7d';
    }
    // Check for Last 30 days (span of 29 days for 30 calendar days)
    if (daysDiff === 29) {
        return 'last-30d';
    }
    // Check for Last 90 days (span of 89 days for 90 calendar days)
    if (daysDiff === 89) {
        return 'last-90d';
    }
    // Check for Last 1 year (span of 364 days for 365 calendar days)
    if (daysDiff === 364) {
        return 'last-1y';
    }
    // Check for Year to date
    if (from.getUTCFullYear() === to.getUTCFullYear() &&
        from.getUTCMonth() === 0 &&
        from.getUTCDate() === 1) {
        return 'ytd';
    }
    // Default to custom for non-matching ranges
    return 'custom';
}
/**
 * Get formatted date strings for a preset (YYYY-MM-DD format)
 * @param preset - The quick filter preset
 * @param today - Optional today date (defaults to new Date())
 * @returns Object with fromDate and toDate as YYYY-MM-DD strings
 */
export function getPresetDateRange(preset, today = new Date()) {
    const fromDate = getPresetFromDate(preset, today);
    return {
        fromDate: formatDateDisplay(fromDate),
        toDate: formatDateDisplay(today),
    };
}
/**
 * Format a date for chart display based on aggregate level
 *
 * @param date - Date string or Date object to format
 * @param aggregateLevel - The aggregation level (DAY, WEEK, MONTH, QUARTER, YEAR)
 * @returns Formatted date string appropriate for the aggregate level
 *
 * @example
 * formatChartDate('2025-12-09', 'DAY') // "Dec 9, 2025"
 * formatChartDate('2025-12-09', 'WEEK') // "Dec 8, 2025" (start of week)
 * formatChartDate('2025-12-09', 'MONTH') // "Dec 2025"
 * formatChartDate('2025-12-09', 'QUARTER') // "Dec 2025" (or "Q4 2025")
 * formatChartDate('2025-12-09', 'YEAR') // "2025"
 */
export function formatChartDate(date, aggregateLevel) {
    if (!date) {
        return '';
    }
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    if (isNaN(dateObj.getTime())) {
        return '';
    }
    switch (aggregateLevel) {
        case 'DAY':
            return format(dateObj, 'MMM d, yyyy');
        case 'WEEK': {
            // Always show start of the week
            const weekStart = startOfWeek(dateObj, { weekStartsOn: 0 }); // Sunday
            return format(weekStart, 'MMM d, yyyy');
        }
        case 'MONTH':
            return format(dateObj, 'MMM yyyy');
        case 'QUARTER':
            return format(dateObj, 'MMM yyyy');
        case 'YEAR':
            return format(dateObj, 'yyyy');
        default:
            return format(dateObj, 'MMM d, yyyy');
    }
}
/**
 * Format a date as a UTC pool timestamp string (e.g. "Aug 26, 2026, 1:05 PM UTC")
 */
export function formatPoolDateTime(date) {
    return `${formatInTimeZone(date, 'UTC', 'MMM d, yyyy, h:mm a')} UTC`;
}
//# sourceMappingURL=dateUtils.js.map